import React from 'react';
import {
  TYPE_ICONS,
  breakdownRows,
  kraveinRows,
  fmtDateTime,
  fmtC,
} from './helpers';
import { useFetchOrderLineItems } from './hooks/useFetchOrderLineItems';
import styles from './PaymentInvoice.module.css';

const COLOR_CLASS = {
  'var(--ink)': styles.valInk,
  'var(--rd)': styles.valRd,
  'var(--or)': styles.valOr,
  'var(--gr)': styles.valGr,
};

const LineItemsList = ({ status, lineItems }) => {
  if (status === 'loading') {
    return <div className={styles.drLoading}>Loading items…</div>;
  }
  if (status === 'error' || lineItems.length === 0) {
    return <div className={styles.drEmpty}>No line items found.</div>;
  }
  return (
    <div className={styles.lineItemsList}>
      {lineItems.map(item => (
        <div
          key={`${item.name}-${item.variantName ?? ''}-${item.quantity}`}
          className={styles.lineItem}
        >
          <div className={styles.lineItemHeader}>
            <span className={styles.lineItemQty}>{item.quantity}×</span>
            <span className={styles.lineItemName}>{item.name}</span>
            <span className={styles.lineItemPrice}>
              ${item.overallPrice.toFixed(2)}
            </span>
          </div>
          {item.variantName && (
            <div className={styles.lineItemSub}>
              {item.variantName} (${item.price.toFixed(2)})
            </div>
          )}
          {item.modifiers.map(mod => (
            <div
              key={`${mod.name}-${mod.variantName ?? ''}`}
              className={styles.lineItemSub}
            >
              {mod.name}
              {mod.variantName ? ` · ${mod.variantName}` : ''} — $
              {mod.pricePerItem.toFixed(2)}
              {mod.quantity > 1 ? ` ×${mod.quantity}` : ''}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

const OrderDrawer = ({ order, showKravein, onClose, userId }) => {
  const { status: lineItemsStatus, lineItems } = useFetchOrderLineItems(
    userId,
    order?.orderReference,
  );

  return (
    <>
      <div className={styles.dov} onClick={onClose} />
      <div className={styles.drawer}>
        <div className={styles.drHd}>
          <div>
            <div className={styles.drTitle}>Order {order.orderReference}</div>
            <div className={styles.drSub}>
              {order.serviceType} · {order.placeName} ·{' '}
              {fmtDateTime(order.orderDate)}
            </div>
          </div>
          <button
            className={`button ${styles.btn} ${styles.btnClose}`}
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        <div className={styles.drBody}>
          <div className={styles.drSec}>
            <div className={styles.drSecTitle}>Order Info</div>
            <div className={styles.drRow}>
              <span className={styles.drLabel}>Order No.</span>
              <span className={styles.drOrdId}>{order.orderReference}</span>
            </div>
            <div className={styles.drRow}>
              <span className={styles.drLabel}>Place</span>
              <span>{order.placeName}</span>
            </div>
            <div className={styles.drRow}>
              <span className={styles.drLabel}>Date &amp; Time</span>
              <span>{fmtDateTime(order.orderDate)}</span>
            </div>
            <div className={styles.drRow}>
              <span className={styles.drLabel}>Order Type</span>
              <span>
                {TYPE_ICONS[order.serviceType] || ''} {order.serviceType}
              </span>
            </div>
          </div>

          <div className={styles.drSec}>
            <div className={styles.drSecTitle}>
              Line Items ({lineItems.length})
            </div>
            <LineItemsList status={lineItemsStatus} lineItems={lineItems} />
          </div>

          <div className={styles.drSec}>
            <div className={styles.drSecTitle}>Payout Details</div>
            <div className={styles.breakdownBox}>
              {breakdownRows(order).map(([label, value, color]) => (
                <div key={label} className={styles.breakdownRow}>
                  <span className={styles.breakdownLabel}>{label}</span>
                  <span
                    className={`${styles.breakdownValue} ${COLOR_CLASS[color] ||
                      ''}`}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {showKravein && (
            <div className={styles.drSec}>
              <div className={styles.drSecTitle}>
                KRAVEiN Charges (charged to customer)
              </div>
              <div className={styles.kraveinBox}>
                {kraveinRows(order).map(([label, value]) => (
                  <div key={label} className={styles.kraveinRow}>
                    <span className={styles.kraveinLabel}>{label}</span>
                    <span className={styles.kraveinValue}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showKravein && (
            <div className={styles.drSec}>
              <div className={styles.drSecTitle}>Application Fee</div>
              <div className={styles.appFeeBox}>
                <div className={styles.appFeeRow}>
                  <span className={styles.appFeeLabel}>Application Fee</span>
                  <span className={styles.appFeeValue}>
                    {(order.applicationFee ?? 0) !== 0
                      ? fmtC(order.applicationFee)
                      : '—'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={styles.drFt}>
          <button
            className={`button ${styles.btn} ${styles.btnSecondary}`}
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};

export default OrderDrawer;
